import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './BibaChat.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BibaChat = () => {
    const [searchParams] = useSearchParams();
    const workflowId = searchParams.get('workflow');
    const [conversations, setConversations] = useState([]);
    const [currentConversation, setCurrentConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [pastedImages, setPastedImages] = useState([]); // Store pasted images with preview URLs
    const [loading, setLoading] = useState(false);
    const [loadingConversations, setLoadingConversations] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [checking, setChecking] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const lastUserMessageRef = useRef(null);
    const fileInputRef = useRef(null);
    const pastedImagesRef = useRef([]);
    const chatInputFormRef = useRef(null);
    const navigate = useNavigate();

    // Check authentication
    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (token) {
            setIsAuthenticated(true);
        } else {
            navigate('/admin/login');
        }
        setChecking(false);
    }, [navigate]);

    // Auto-scroll to last user message when new messages arrive
    const scrollToLastUserMessage = () => {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
            // First try to scroll to the last user message
            if (lastUserMessageRef.current) {
                lastUserMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            } else if (messagesEndRef.current) {
                // Fallback to scrolling to the end if no user message ref is set
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        });
    };

    useEffect(() => {
        scrollToLastUserMessage();
    }, [messages]);

    // Load conversations on mount
    useEffect(() => {
        if (isAuthenticated) {
            loadConversations();
        }
    }, [isAuthenticated]);

    // Update ref when pastedImages changes
    useEffect(() => {
        pastedImagesRef.current = pastedImages;
    }, [pastedImages]);

    // Cleanup object URLs on unmount
    useEffect(() => {
        return () => {
            pastedImagesRef.current.forEach(img => {
                if (img.previewUrl) {
                    URL.revokeObjectURL(img.previewUrl);
                }
            });
        };
    }, []);

    // Drag and drop handlers
    useEffect(() => {
        const form = chatInputFormRef.current;
        if (!form) return;

        const handleDragEnter = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.dataTransfer.types.includes('Files')) {
                setIsDragging(true);
            }
        };

        const handleDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const handleDragLeave = (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Only set dragging to false if we're leaving the form area
            if (!form.contains(e.relatedTarget)) {
                setIsDragging(false);
            }
        };

        const handleDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            const files = Array.from(e.dataTransfer.files);
            if (files.length === 0) return;

            // Filter for images and documents only
            const allowedTypes = /\.(jpg|jpeg|png|gif|webp|pdf|txt|doc|docx|rtf)$/i;
            const validFiles = files.filter(file => allowedTypes.test(file.name) || file.type.startsWith('image/'));
            
            if (validFiles.length === 0) {
                alert('Please drop image files (jpg, png, gif, webp) or documents (pdf, txt, doc, docx, rtf)');
                return;
            }

            if (validFiles.length > 5) {
                alert('You can only upload up to 5 files at once.');
                return;
            }

            // Separate images and documents
            const imageFiles = [];
            const documentFiles = [];

            validFiles.forEach(file => {
                if (file.type.startsWith('image/')) {
                    const previewUrl = URL.createObjectURL(file);
                    imageFiles.push({
                        file: file,
                        previewUrl: previewUrl
                    });
                } else {
                    documentFiles.push(file);
                }
            });

            // Add images to pastedImages
            if (imageFiles.length > 0) {
                const totalCount = pastedImages.length + selectedFiles.length + imageFiles.length;
                if (totalCount > 5) {
                    alert('You can only attach up to 5 files/images at once. Please remove some first.');
                    imageFiles.forEach(img => URL.revokeObjectURL(img.previewUrl));
                    return;
                }
                setPastedImages(prev => [...prev, ...imageFiles]);
            }

            // Add documents to selectedFiles
            if (documentFiles.length > 0) {
                const totalCount = pastedImages.length + selectedFiles.length + documentFiles.length;
                if (totalCount > 5) {
                    alert('You can only attach up to 5 files/images at once. Please remove some first.');
                    return;
                }
                setSelectedFiles(prev => [...prev, ...documentFiles]);
            }
        };

        form.addEventListener('dragenter', handleDragEnter);
        form.addEventListener('dragover', handleDragOver);
        form.addEventListener('dragleave', handleDragLeave);
        form.addEventListener('drop', handleDrop);

        return () => {
            form.removeEventListener('dragenter', handleDragEnter);
            form.removeEventListener('dragover', handleDragOver);
            form.removeEventListener('dragleave', handleDragLeave);
            form.removeEventListener('drop', handleDrop);
        };
    }, [pastedImages, selectedFiles]);

    const loadConversations = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_URL}/chat/conversations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setConversations(response.data);
            setLoadingConversations(false);
        } catch (error) {
            console.error('Error loading conversations:', error);
            if (error.response?.status === 401) {
                navigate('/admin/login');
            }
            setLoadingConversations(false);
        }
    };

    const loadConversation = async (conversationId) => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${API_URL}/chat/conversations/${conversationId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCurrentConversation(response.data.conversation);
            setMessages(response.data.messages);
        } catch (error) {
            console.error('Error loading conversation:', error);
        }
    };

    const createNewConversation = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.post(
                `${API_URL}/chat/conversations`,
                { title: 'New Conversation' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const newConversation = response.data;
            setConversations([newConversation, ...conversations]);
            setCurrentConversation(newConversation);
            setMessages([]);
        } catch (error) {
            console.error('Error creating conversation:', error);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 5) {
            alert('You can only upload up to 5 files at once.');
            return;
        }
        addFilesToSelection(files);
    };

    const addFilesToSelection = (files) => {
        setSelectedFiles(prev => {
            const currentFileCount = prev.length;
            const newFileCount = files.length;
            
            if (currentFileCount + newFileCount > 5) {
                alert('You can only upload up to 5 files at once. Please remove some files first.');
                return prev; // Return previous state if limit exceeded
            }
            
            return [...prev, ...files];
        });
    };

    const handlePaste = async (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        const imageFiles = [];
        const timestamp = Date.now();
        
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            
            // Check if the item is an image
            if (item.type.indexOf('image') !== -1) {
                const blob = item.getAsFile();
                if (blob) {
                    // Create a File object from the blob with a meaningful name
                    const extension = blob.type.split('/')[1] || 'png';
                    const fileName = `pasted-image-${timestamp}-${i}.${extension}`;
                    const file = new File([blob], fileName, { type: blob.type });
                    
                    // Create preview URL for the image
                    const previewUrl = URL.createObjectURL(blob);
                    
                    imageFiles.push({
                        file: file,
                        previewUrl: previewUrl
                    });
                }
            }
        }

        if (imageFiles.length > 0) {
            e.preventDefault(); // Prevent the default paste behavior
            
            // Check total count limit
            const totalCount = pastedImages.length + selectedFiles.length + imageFiles.length;
            if (totalCount > 5) {
                alert('You can only attach up to 5 files/images at once. Please remove some first.');
                // Clean up preview URLs
                imageFiles.forEach(img => URL.revokeObjectURL(img.previewUrl));
                return;
            }
            
            setPastedImages(prev => [...prev, ...imageFiles]);
        }
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removePastedImage = (index) => {
        setPastedImages(prev => {
            // Clean up the object URL before removing
            if (prev[index]?.previewUrl) {
                URL.revokeObjectURL(prev[index].previewUrl);
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        const totalFiles = selectedFiles.length + pastedImages.length;
        if ((!inputMessage.trim() && totalFiles === 0) || loading) return;

        // Create conversation if none exists
        if (!currentConversation) {
            await createNewConversation();
            return;
        }

        const messageContent = inputMessage.trim();
        setInputMessage('');
        
        // Combine selected files and pasted images
        const filesToSend = [...selectedFiles, ...pastedImages.map(img => img.file)];
        const pastedImagesToClean = [...pastedImages];
        
        setSelectedFiles([]);
        setPastedImages([]);
        setLoading(true);

        // Optimistically add user message to UI
        const tempUserMessage = {
            role: 'user',
            content: messageContent || `[${filesToSend.length} file(s) attached]`,
            timestamp: new Date()
        };
        setMessages([...messages, tempUserMessage]);

        try {
            const token = localStorage.getItem('adminToken');
            
            // Create FormData if files are present, otherwise use JSON
            let requestData;
            let headers = { Authorization: `Bearer ${token}` };
            
            if (filesToSend.length > 0) {
                requestData = new FormData();
                requestData.append('content', messageContent || '');
                filesToSend.forEach((file) => {
                    requestData.append('files', file);
                });
                // Don't set Content-Type header for FormData, let browser set it with boundary
            } else {
                requestData = { content: messageContent };
                headers['Content-Type'] = 'application/json';
            }

            const response = await axios.post(
                `${API_URL}/chat/conversations/${currentConversation._id}/messages`,
                requestData,
                { headers }
            );

            // Replace temp message with actual messages from server
            setMessages(prev => [
                ...prev.slice(0, -1),
                response.data.userMessage,
                response.data.assistantMessage
            ]);

            // Reload conversations to update the list (title might have changed)
            loadConversations();
            
            // Clean up preview URLs after successful send
            pastedImagesToClean.forEach(img => {
                if (img.previewUrl) {
                    URL.revokeObjectURL(img.previewUrl);
                }
            });
        } catch (error) {
            console.error('Error sending message:', error);
            // Remove temp message on error
            setMessages(prev => prev.slice(0, -1));
            alert('Failed to send message. Please try again.');
            
            // Clean up preview URLs on error
            pastedImagesToClean.forEach(img => {
                if (img.previewUrl) {
                    URL.revokeObjectURL(img.previewUrl);
                }
            });
        } finally {
            setLoading(false);
            // Clear file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const deleteConversation = async (conversationId, e) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this conversation?')) {
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${API_URL}/chat/conversations/${conversationId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setConversations(conversations.filter(c => c._id !== conversationId));
            if (currentConversation?._id === conversationId) {
                setCurrentConversation(null);
                setMessages([]);
            }
        } catch (error) {
            console.error('Error deleting conversation:', error);
            alert('Failed to delete conversation.');
        }
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);

        if (diffInHours < 24) {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else if (diffInHours < 168) { // 7 days
            return date.toLocaleDateString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    if (checking) {
        return <div className="loading">Checking authentication...</div>;
    }

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="biba-chat-wrapper">
            <div className="biba-chat-header">
                <button onClick={() => navigate('/admin')} className="back-btn">
                    ← Back to Dashboard
                </button>
                <h1>Biba AI Chat</h1>
            </div>
            <div className="biba-chat">
                {/* Sidebar */}
                <div className="chat-sidebar">
                    <div className="sidebar-header">
                        <h2>Biba AI Chat</h2>
                        <button onClick={createNewConversation} className="new-chat-btn">
                            + New Chat
                        </button>
                    </div>
                    <div className="conversations-list">
                        {loadingConversations ? (
                            <div className="loading-conversations">Loading...</div>
                        ) : conversations.length === 0 ? (
                            <div className="no-conversations">No conversations yet</div>
                        ) : (
                            conversations.map(conv => (
                                <div
                                    key={conv._id}
                                    className={`conversation-item ${currentConversation?._id === conv._id ? 'active' : ''}`}
                                    onClick={() => loadConversation(conv._id)}
                                >
                                    <div className="conversation-title">{conv.title}</div>
                                    <div className="conversation-time">{formatTime(conv.updatedAt)}</div>
                                    <button
                                        className="delete-conversation-btn"
                                        onClick={(e) => deleteConversation(conv._id, e)}
                                        title="Delete conversation"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Main Chat Area */}
                <div className="chat-main">
                    {!currentConversation ? (
                        <div className="chat-welcome">
                            <h1>Welcome to Biba AI</h1>
                            <p>Start a new conversation to chat with AI</p>
                            <button onClick={createNewConversation} className="start-chat-btn">
                                Start New Chat
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="chat-messages" ref={messagesContainerRef}>
                                {messages.length === 0 ? (
                                    <div className="no-messages">
                                        <p>Start your conversation with Biba AI</p>
                                    </div>
                                ) : (
                                    (() => {
                                        // Find the index of the last user message
                                        let lastUserMessageIndex = -1;
                                        for (let i = messages.length - 1; i >= 0; i--) {
                                            if (messages[i].role === 'user') {
                                                lastUserMessageIndex = i;
                                                break;
                                            }
                                        }
                                        
                                        return messages.map((msg, index) => {
                                            const isLastUserMessage = index === lastUserMessageIndex;
                                            
                                            return (
                                                <div 
                                                    key={index} 
                                                    className={`message ${msg.role}`}
                                                    ref={isLastUserMessage ? lastUserMessageRef : null}
                                                >
                                                    <div className="message-avatar">
                                                        {msg.role === 'user' ? '👤' : '🤖'}
                                                    </div>
                                                    <div className="message-content">
                                                        <div className="message-text">{msg.content}</div>
                                                        <div className="message-time">{formatTime(msg.timestamp)}</div>
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()
                                )}
                                {loading && (
                                    <div className="message assistant">
                                        <div className="message-avatar">🤖</div>
                                        <div className="message-content">
                                            <div className="message-text typing-indicator">
                                                <span></span><span></span><span></span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            <form 
                                onSubmit={sendMessage} 
                                className={`chat-input-form ${isDragging ? 'dragging' : ''}`}
                                ref={chatInputFormRef}
                            >
                                {pastedImages.length > 0 && (
                                    <div className="pasted-images-preview">
                                        {pastedImages.map((imgData, index) => (
                                            <div key={index} className="pasted-image-item">
                                                <img 
                                                    src={imgData.previewUrl} 
                                                    alt={`Pasted ${index + 1}`}
                                                    className="pasted-image-preview"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removePastedImage(index)}
                                                    className="remove-pasted-image-btn"
                                                    disabled={loading}
                                                    title="Remove image"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {selectedFiles.length > 0 && (
                                    <div className="selected-files">
                                        {selectedFiles.map((file, index) => (
                                            <div key={index} className="file-item">
                                                <span className="file-name">{file.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(index)}
                                                    className="remove-file-btn"
                                                    disabled={loading}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="input-row">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileSelect}
                                        multiple
                                        accept="image/*,.pdf,.txt,.doc,.docx,.rtf"
                                        className="file-input"
                                        disabled={loading}
                                        id="file-input"
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="file-input" className="file-input-label" title="Upload image or document (or drag & drop)">
                                        📎
                                    </label>
                                    {isDragging && (
                                        <div className="drag-overlay">
                                            <div className="drag-overlay-content">
                                                <div className="drag-icon">📤</div>
                                                <div className="drag-text">Drop images or documents here</div>
                                            </div>
                                        </div>
                                    )}
                                    <input
                                        type="text"
                                        value={inputMessage}
                                        onChange={(e) => setInputMessage(e.target.value)}
                                        onPaste={handlePaste}
                                        placeholder={selectedFiles.length > 0 || pastedImages.length > 0 
                                            ? `Type your message... (${selectedFiles.length + pastedImages.length} file${selectedFiles.length + pastedImages.length > 1 ? 's' : ''} attached)`
                                            : "Type your message... (or paste/drag images)"}
                                        className="chat-input"
                                        disabled={loading}
                                    />
                                    <button type="submit" className="send-btn" disabled={loading || (!inputMessage.trim() && selectedFiles.length === 0 && pastedImages.length === 0)}>
                                        Send
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BibaChat;
